import React, { useState } from "react";
import { Navigate } from "react-router-dom";
import {
  Users,
  Shield,
  User,
  Key,
  Plus,
  Search,
  CheckCircle2,
} from "lucide-react";
import { useDashboard } from "../context/DashboardContext";
import { ROLE_LABELS, ROLE_BADGE_CLASSES } from "../roles";
import "./Controls.css";

const Controls = () => {
  const {
    roleLabel,
    canManageUsers,
    creatableRoles,
    users,
    loadingUsers,
    handleCreateUser,
  } = useDashboard();

  const [newUser, setNewUser] = useState({
    username: "",
    password: "",
    role: creatableRoles[0] || "",
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // If unauthorized (e.g. Seller), redirect to overview
  if (!canManageUsers) {
    return <Navigate to="/dashboard" replace />;
  }

  const onSubmitUser = async (e) => {
    e.preventDefault();
    if (!newUser.username.trim() || !newUser.password.trim()) {
      alert("Please fill in both operator username and temporary password.");
      return;
    }

    setSubmitting(true);
    try {
      await handleCreateUser({
        username: newUser.username.trim(),
        password: newUser.password.trim(),
        targetRole: newUser.role || creatableRoles[0],
      });
      setNewUser({
        username: "",
        password: "",
        role: creatableRoles[0] || "",
      });
    } catch {
      // Error handled by notify
    } finally {
      setSubmitting(false);
    }
  };

  const filteredUsers = users.filter((u) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      u.username?.toLowerCase().includes(q) ||
      u.role?.toLowerCase().includes(q) ||
      u.createdBy?.toLowerCase().includes(q)
    );
  });

  return (
    <div className="bf-controls-container">
      {/* Header Banner */}
      <section className="bf-controls-header">
        <div>
          <h1 className="bf-controls-title">
            <Users size={22} color="var(--blue)" />
            <span>OPERATOR CONTROLS & DOWNLINE CLEARANCE</span>
          </h1>
          <p className="bf-controls-subtitle">
            Delegate operational access and manage tier permissions for operators within your chain.
          </p>
        </div>

        <div className="bf-operator-pill">
          <span className="tech-label">AUTHORIZED BY:</span>
          <span className="bf-operator-name">{roleLabel}</span>
        </div>
      </section>

      {/* Operator Provisioning Form */}
      <section className="bf-panel" id="provision-panel">
        <div className="bf-panel-header">
          <div className="bf-panel-title">
            <Shield size={16} color="var(--blue)" />
            <span>PROVISION NEW DOWNLINE OPERATOR</span>
          </div>
          <span className="tech-badge blue">ACCESS DELEGATION</span>
        </div>

        <form onSubmit={onSubmitUser} className="bf-form-row">
          <div className="bf-form-group">
            <label className="tech-label" htmlFor="ctrl-target-role">
              <Shield size={12} />
              TARGET CLEARANCE ROLE
            </label>
            <select
              id="ctrl-target-role"
              className="bf-select-ctrl font-mono"
              value={newUser.role || creatableRoles[0]}
              onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
              required
            >
              {creatableRoles.map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABELS[r]} [{r.toUpperCase()}]
                </option>
              ))}
            </select>
          </div>

          <div className="bf-form-group">
            <label className="tech-label" htmlFor="ctrl-username">
              <User size={12} />
              OPERATOR USERNAME
            </label>
            <input
              id="ctrl-username"
              className="bf-input-ctrl font-mono"
              type="text"
              placeholder="e.g. operator_beta"
              value={newUser.username}
              onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
              required
            />
          </div>

          <div className="bf-form-group">
            <label className="tech-label" htmlFor="ctrl-password">
              <Key size={12} />
              TEMPORARY PASSWORD
            </label>
            <input
              id="ctrl-password"
              className="bf-input-ctrl font-mono"
              type="password"
              placeholder="••••••••••••"
              value={newUser.password}
              onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
              required
            />
          </div>

          <button
            type="submit"
            className="bf-btn-action blue"
            disabled={submitting}
          >
            {submitting ? (
              <span>PROVISIONING...</span>
            ) : (
              <>
                <Plus size={16} strokeWidth={2.5} />
                PROVISION OPERATOR
              </>
            )}
          </button>
        </form>
      </section>

      {/* Downline Operators List Console */}
      <section className="bf-panel" id="operators-list-panel">
        <div className="bf-panel-header">
          <div className="bf-panel-title">
            <Users size={16} color="var(--blue)" />
            <span>DOWNLINE OPERATOR DIRECTORY</span>
          </div>

          <div className="bf-search-box" style={{ maxWidth: "320px", width: "100%" }}>
            <Search size={14} color="var(--text-3)" />
            <input
              type="text"
              placeholder="Search operator, creator, or role..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        {loadingUsers ? (
          <div className="bf-empty-state">
            <div className="status-dot blue pulse" style={{ width: "12px", height: "12px" }} />
            <span>FETCHING OPERATOR DIRECTORY FROM CLUSTER...</span>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="bf-empty-state">
            <Users size={36} strokeWidth={1.5} color="var(--text-3)" />
            <span>
              {searchQuery ? "NO MATCHING OPERATORS FOUND" : "NO DOWNLINE OPERATORS PROVISIONED YET"}
            </span>
          </div>
        ) : (
          <div className="bf-user-grid">
            {filteredUsers.map((u) => {
              const badgeClass = ROLE_BADGE_CLASSES[u.role] || "neutral";
              const label = ROLE_LABELS[u.role] || u.role;
              const initial = (u.username || "O").charAt(0).toUpperCase();

              return (
                <div className="bf-operator-card" key={u._id || u.username}>
                  <div className="bf-operator-main">
                    <div className="bf-operator-avatar">{initial}</div>
                    <div className="bf-operator-details">
                      <span className="bf-operator-username">@{u.username}</span>
                      <span className="bf-operator-creator">
                        PROVISIONED BY: <strong>{u.createdBy || "ROOT"}</strong>
                      </span>
                    </div>
                  </div>

                  <div className="bf-operator-meta-right">
                    <span className={`tech-badge ${badgeClass}`}>{label}</span>
                    <span
                      style={{
                        fontFamily: "var(--font-mono)",
                        fontSize: "10.5px",
                        color: "var(--green-0)",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      <CheckCircle2 size={11} /> ACTIVE
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
};

export default Controls;
